import { Box, Grid, Link, List, ListItem, ListItemText, styled, Typography } from '@mui/material';
import { colors } from '../../../../styles/fontStyles';
import { useThemeContext } from '../../../../contexts/BaseThemeContextProvider';

const AboutTheTestMethodology = () => {
  const { theme } = useThemeContext();

  const InfoCard = styled(Box)({
    backgroundColor:
      theme.custom.pages.howTo.aboutTheTestMethodology.renderUnderstandingTerminology.infoCard.boxStyles
        .backgroundColor,
    borderRadius: '16px',
    padding: '24px',
    height: '100%',
  });

  const headerTextStyle = {
    color:
      theme.custom.pages.howTo.aboutTheTestMethodology.renderUnderstandingTerminology.infoCard.headerTextStyle.color,
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '20px',
    letterSpacing: '0.25px',
  };

  const descriptionTextStyle = {
    color:
      theme.custom.pages.howTo.aboutTheTestMethodology.renderUnderstandingTerminology.infoCard.descriptionTextStyle
        .color,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.25px',
  };

  const StyledLink = styled(Link)({
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    color: theme.custom.pages.howTo.aboutTheTestMethodology.renderUsefulReferences.styledLink.color,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    textDecoration: 'underline',
    textDecorationStyle: 'solid',
    textDecorationSkipInk: 'auto',
    textDecorationThickness: 'auto',
    textUnderlineOffset: 'auto',
    textUnderlinePosition: 'from-font',
  });

  const renderUnderstandingTerminology = () => {
    return (
      <Box sx={{ textAlign: 'left' }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: '22px',
            fontWeight: 400,
            color:
              theme.custom.pages.howTo.aboutTheTestMethodology.renderUnderstandingTerminology.typography.body1.color,
          }}
        >
          Understanding some terminology
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color:
              theme.custom.pages.howTo.aboutTheTestMethodology.renderUnderstandingTerminology.typography.body2.color,
            marginBottom: '32px',
          }}
        >
          Comparing tests and understanding scientific papers can be challenging. Here's some of the terminology you
          might come across in this guide and in other places.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InfoCard>
              <Typography sx={descriptionTextStyle}>
                <Typography component="span" sx={headerTextStyle}>
                  CLIA (Clinical Laboratory Improvement Amendments).
                </Typography>{' '}
                CLIA regulates laboratory tests on patient specimens. CLIA covers quality, accuracy, and reliability of
                tests used in a laboratory setting. It requires the laboratory to be certified, to employ qualified
                staff for specific regulated roles, and to maintain a laboratory information system. The Relentless
                Health Lab is CLIA certified for its PFAS test (license 29D2302788, NV License #12531-LIC-0).
              </Typography>
            </InfoCard>
          </Grid>

          <Grid item xs={12} sm={6}>
            <InfoCard>
              <Typography sx={descriptionTextStyle}>
                <Typography component="span" sx={headerTextStyle}>
                  Nanograms per milliliter (ng/mL).
                </Typography>{' '}
                This is the standard measure for levels of PFAS in whole blood or blood plasma. This is sometimes also
                called parts per billion (ppb) or micrograms per liter (ug/L).
              </Typography>
            </InfoCard>
          </Grid>

          <Grid item xs={12} sm={6}>
            <InfoCard>
              <Typography sx={descriptionTextStyle}>
                <Typography component="span" sx={headerTextStyle}>
                  Reference Range.
                </Typography>{' '}
                This is the typical range of results seen by a given lab for a given substance. This is affected by the
                population being tested, sensitivity of the equipment, and the quality of the lab. Relentless Health is
                currently running a clinical study to set the reference ranges for our PFAS test.
              </Typography>
            </InfoCard>
          </Grid>

          <Grid item xs={12} sm={6}>
            <InfoCard>
              <Typography sx={descriptionTextStyle}>
                <Typography component="span" sx={headerTextStyle}>
                  Reporting Limit.
                </Typography>{' '}
                This is the minimum level at which the lab is prepared to state with complete confidence that this
                result is not a false positive and where we have proven that we can measure (quantify) the substance
                precisely. Samples may be marked as “Below Reportable Range” or “Above Reportable Range”.
              </Typography>
            </InfoCard>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderUsefulReferences = () => {
    return (
      <Box
        sx={{
          margin: '32px auto 0 auto',
          width: '100%',
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: '22px',
            textAlign: 'left',
            fontWeight: 400,
            color: theme.custom.pages.howTo.aboutTheTestMethodology.renderUsefulReferences.typography.body1.color,
            marginBottom: '8px',
          }}
        >
          Useful References
        </Typography>
        <List
          dense
          sx={{
            color: theme.custom.pages.howTo.aboutTheTestMethodology.renderUsefulReferences.list.color,
            fontSize: '12x',
            fontWeight: 400,
            lineHeight: '16px',
            marginLeft: '5px',
          }}
        >
          <ListItem disableGutters sx={{ paddingTop: 0, paddingBottom: 0 }}>
            <ListItemText
              primary={
                <>
                  1. National academies of science, engineering and medicine Guidance on PFAS Exposure, Testing, and
                  Clinical Follow-Up:{' '}
                  <StyledLink
                    href="https://nap.nationalacademies.org/resource/26156/interactive/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://nap.nationalacademies.org/resource/26156/interactive/
                  </StyledLink>
                </>
              }
            />
          </ListItem>

          <ListItem disableGutters sx={{ paddingTop: 0, paddingBottom: 0 }}>
            <ListItemText
              primary={
                <>
                  2. Poothong, et Al. Distribution of Novel and Well-Known Poly- and Perfluoroalkyl Substances (PFASs)
                  in Human Serum, Plasma, and Whole Blood. Environmental Science & Technology 2017 &nbsp;
                  <StyledLink
                    href="https://pubmed.ncbi.nlm.nih.gov/29056041/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://pubmed.ncbi.nlm.nih.gov/29056041/
                  </StyledLink>
                </>
              }
            />
          </ListItem>

          <ListItem disableGutters sx={{ paddingTop: 0, paddingBottom: 0 }}>
            <ListItemText
              primary={
                <>
                  3. Carignan, et Al. Self-Collection Blood Test for PFASs: Comparing Volumetric Microsamplers with a
                  Traditional Serum Approach. Environmental Science & Technology 2023 &nbsp;
                  <StyledLink
                    href="https://pubmed.ncbi.nlm.nih.gov/29056041/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://pubs.acs.org/doi/10.1021/acs.est.2c09852
                  </StyledLink>
                </>
              }
            />
          </ListItem>
        </List>
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderBottomRightRadius: '16px',
          borderBottomLeftRadius: '16px',
          padding: { xs: '24px 16px', sm: '24px 40px 40px 40px' },
          backgroundColor: theme.custom.pages.howTo.aboutTheTestMethodology.backgroundColor,
        }}
      >
        {renderUnderstandingTerminology()}
        {renderUsefulReferences()}
      </Box>
    </>
  );
};

export default AboutTheTestMethodology;
